import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import { theme } from '../theme.js';
import { useAuth, useAccess } from '../context/AuthContext.js';
import { useAssets, useTeams, useCreateRun } from '../lib/queries.js';
import { assetTypeIcon, assetTypeLabel } from '../lib/asset-types.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';

/**
 * Start an on-demand (off-schedule) check: pick an asset and go. It's
 * assigned to your team; admins choose the team. If your team already has
 * an open on-demand check on that asset, you're taken back to it.
 */
export function StartCheckPage() {
  const nav = useNavigate();
  const back = useBack('/checks');
  const { user } = useAuth();
  const { me, isAdmin } = useAccess();
  const orgId = user?.orgId ?? '';

  const { data: assets = [], isLoading } = useAssets(orgId);
  const { data: teams = [] } = useTeams(orgId);
  const createMut = useCreateRun(orgId);
  const [teamId, setTeamId] = useState(me?.team?.id ?? '');

  const needsTeam = !me?.team && !isAdmin;

  function start(assetId: string) {
    createMut.mutate(
      { assetId, teamId: isAdmin && teamId ? teamId : undefined },
      { onSuccess: (run) => nav(`/checks/${run.id}`, { replace: true }) },
    );
  }

  return (
    <PageShell noNav>
      <div style={{
        padding: '12px 16px', background: theme.surface, borderBottom: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 5,
      }}>
        <button onClick={back} style={iconBtn} aria-label="Back"><Icon name="back" size={18} /></button>
        <div style={{ fontSize: 17, fontWeight: 700, color: theme.text }}>Start a check</div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', background: theme.surface }}>
        <div style={{ padding: '12px 18px', fontSize: 13, color: theme.textMuted, lineHeight: 1.5, background: theme.bg }}>
          An on-demand check is separate from the schedule — use it after a job, an incident, or whenever
          something needs a look.
        </div>

        {needsTeam && (
          <div style={{
            margin: '12px 16px', padding: '10px 12px', borderRadius: 10, fontSize: 13,
            background: theme.status.warn.bg, color: theme.status.warn.fg,
          }}>
            You need to be on a team to start a check — ask an admin to add you.
          </div>
        )}

        {isAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 18px', fontSize: 13, color: theme.textDim }}>
            Team
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)} required style={{
              flex: 1, padding: '8px 10px', borderRadius: 8, border: `1px solid ${theme.border}`,
              background: theme.surface, color: theme.text, fontFamily: theme.font, fontSize: 13,
            }}>
              <option value="">Choose a team…</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
        )}

        {isLoading && <div style={{ padding: 20, color: theme.textMuted, fontSize: 14 }}>Loading…</div>}
        {assets.map((a) => {
          const disabled = needsTeam || (isAdmin && !teamId) || createMut.isPending;
          return (
            <button key={a.id} disabled={disabled} onClick={() => start(a.id)} style={{
              display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
              padding: '13px 18px', background: theme.surface, border: 'none', fontFamily: theme.font,
              borderBottom: `1px solid ${theme.border}`, cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.5 : 1,
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: 8, flexShrink: 0,
                background: theme.surface2, border: `1px solid ${theme.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textDim,
              }}>
                <Icon name={assetTypeIcon(a.assetType)} size={18} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{a.name}</div>
                <div style={{ fontSize: 12, color: theme.textMuted }}>
                  {[assetTypeLabel(a.assetType), a.registration, `${a.equipmentCount ?? 0} items`].filter(Boolean).join(' · ')}
                </div>
              </div>
              <Icon name="chevron" size={16} style={{ color: theme.textMuted }} />
            </button>
          );
        })}
      </div>
    </PageShell>
  );
}

const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`,
  background: theme.surface2, color: theme.text, borderRadius: 10,
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
